import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from './firebase';
import { findUserByEmail } from './userService';

const RELATION_COLLECTION = 'usuarios_relacionamentos';

const buildRelationKey = (userA, userB) => [userA, userB].sort().join('_');

export const sendFriendRequest = async (currentUser, targetEmail) => {
  if (!currentUser || !targetEmail) {
    throw new Error('Dados inválidos para convite');
  }

  const receptor = await findUserByEmail(targetEmail);
  if (!receptor) {
    throw new Error('Nenhum usuário foi encontrado com esse email.');
  }
  if (receptor.id === currentUser.uid) {
    throw new Error('Você não pode se convidar.');
  }

  const relationKey = buildRelationKey(currentUser.uid, receptor.id);
  const relationQuery = query(
    collection(db, RELATION_COLLECTION),
    where('relation_key', '==', relationKey)
  );
  const relationSnapshot = await getDocs(relationQuery);
  if (!relationSnapshot.empty) {
    throw new Error('Você já possui uma relação com esse usuário.');
  }

  await addDoc(collection(db, RELATION_COLLECTION), {
    id_user_solicitante: currentUser.uid,
    id_user_receptor: receptor.id,
    status: 'pendente',
    relation_key: relationKey,
    solicitante_nome: currentUser.displayName || currentUser.email,
    solicitante_email: (currentUser.email || '').toLowerCase(),
    receptor_nome: receptor.displayName || receptor.email,
    receptor_email: (receptor.email || '').toLowerCase(),
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });
};

const shapeRelationship = (docSnapshot, currentUserId) => {
  const data = docSnapshot.data();
  const isRequester = data.id_user_solicitante === currentUserId;
  const otherUser = isRequester
    ? { id: data.id_user_receptor, nome: data.receptor_nome, email: data.receptor_email }
    : { id: data.id_user_solicitante, nome: data.solicitante_nome, email: data.solicitante_email };

  return {
    id: docSnapshot.id,
    status: data.status,
    isRequester,
    otherUser,
    raw: data
  };
};

export const fetchRelationships = async (currentUserId) => {
  if (!currentUserId) return [];

  const relationsRef = collection(db, RELATION_COLLECTION);
  const outgoingSnapshot = await getDocs(
    query(relationsRef, where('id_user_solicitante', '==', currentUserId))
  );
  const incomingSnapshot = await getDocs(
    query(relationsRef, where('id_user_receptor', '==', currentUserId))
  );

  const combined = new Map();
  outgoingSnapshot.forEach((docSnapshot) => {
    combined.set(docSnapshot.id, shapeRelationship(docSnapshot, currentUserId));
  });
  incomingSnapshot.forEach((docSnapshot) => {
    combined.set(docSnapshot.id, shapeRelationship(docSnapshot, currentUserId));
  });

  return Array.from(combined.values());
};

export const respondToFriendRequest = async (requestId, accept) => {
  const docRef = doc(db, RELATION_COLLECTION, requestId);
  if (accept) {
    await updateDoc(docRef, {
      status: 'ativo',
      updated_at: serverTimestamp()
    });
  } else {
    await deleteDoc(docRef);
  }
};

export const cancelFriendRequest = async (requestId) => {
  await deleteDoc(doc(db, RELATION_COLLECTION, requestId));
};

export const endFriendship = async (relationId) => {
  await deleteDoc(doc(db, RELATION_COLLECTION, relationId));
};

export const countActiveFriends = async (userId) => {
  if (!userId) return 0;
  const relationsRef = collection(db, RELATION_COLLECTION);
  const [outgoingSnapshot, incomingSnapshot] = await Promise.all([
    getDocs(
      query(
        relationsRef,
        where('id_user_solicitante', '==', userId),
        where('status', '==', 'ativo')
      )
    ),
    getDocs(
      query(
        relationsRef,
        where('id_user_receptor', '==', userId),
        where('status', '==', 'ativo')
      )
    )
  ]);

  const uniqueIds = new Set();
  outgoingSnapshot.forEach((docSnapshot) => uniqueIds.add(docSnapshot.id));
  incomingSnapshot.forEach((docSnapshot) => uniqueIds.add(docSnapshot.id));
  return uniqueIds.size;
};
